/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Notes — right-panel surface (Figma Ping-Desktop 3746:7690 / 8103 /
// 8144 / 8691 / 8609 / 8124).
//
// A single ~409px-wide column with an internal navigation state machine:
//
//   ROOT   — folder tree. Header "Notes" + search icon + "New Folder +".
//            Each folder is a row: chevron + name + (default folder) a
//            3-dot menu. "New Folder +" reveals an inline name input that
//            creates a folder on submit (empty name = no-op).
//   FOLDER — opened folder. Breadcrumb "Notes > {folder}", header
//            "New note +" (+ 3-dot when not the default folder). Note
//            rows = bold title + gray preview (first body line) + date.
//   NOTE   — editor. Breadcrumb "Notes > {title}", 3-dot + "Save".
//            Editable title, date, multiline body, formatting toolbar.
//   EMPTY  — shown at ROOT when there are no notes AND no remembered
//            folders: Ping glyph + tagline + indigo "New note +".
//
// FOLDERS-ON-TAGS: the flat `NoteRecord` has no folder field, so a note's
// FIRST tag is treated as its folder name. Notes with no tag live in the
// default folder ("My notes"). Creating a folder = remembering its name
// in local state (an empty folder has no member notes yet); adding a note
// to it tags the note. Renaming/deleting a folder rewrites the first tag
// on every member note. The "favorite" tag (legacy NotesScreen) is kept
// out of folder resolution so the two surfaces don't fight over tags.
//
// PRESENTATIONAL + platform-agnostic (RN + NativeWind). The host owns the
// LOCAL-FIRST persist + DeviceGroup mirror; this component only renders
// `notes` and calls `onUpsert` / `onDelete`. className-first; inline
// `style` is used only for the runtime-computed panel width.

import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Bold,
	ChevronDown,
	ChevronLeft,
	Code,
	IndentIncrease,
	Italic,
	Link as LinkIcon,
	List,
	ListOrdered,
	MoreVertical,
	Plus,
	Search,
	SquareCode,
	Strikethrough,
	Underline,
} from "lucide-react-native";
import {
	FlatList,
	type ListRenderItem,
	Pressable,
	Text,
	TextInput,
	View,
} from "react-native";

import { useColorScheme } from "../foundations/colorScheme";
import { PingLogomark } from "../icons";
import type { NoteRecord } from "../sync/persistenceAdapters";
import { buildPreview } from "./legacy";
import {
	insertCodeBlock,
	insertLink,
	type Selection as MdSelection,
	toggleLinePrefix,
	toggleWrap,
	type WrapKind,
} from "./markdownEdit";
import type { NotesSyncState } from "./NotesScreen";

export type { NotesSyncState } from "./NotesScreen";

// ─────────────────────────────────────────────────────────────────────
// Folder model (derived from tags)

/** Default folder for notes with no folder tag. */
const DEFAULT_FOLDER = "My notes";

/** Legacy favorites tag — excluded from folder resolution. */
const FAVORITE_TAG = "favorite";

/** First non-favorite tag → folder name; else the default folder. */
function folderOf(note: NoteRecord): string {
	const tag = (note.tags ?? []).find((t) => t !== FAVORITE_TAG);
	return tag !== undefined && tag.length > 0 ? tag : DEFAULT_FOLDER;
}

/** Replace a note's folder tag, preserving the favorite tag if present. */
function tagsForFolder(note: NoteRecord, folder: string): string[] {
	const keep = (note.tags ?? []).filter((t) => t === FAVORITE_TAG);
	if (folder === DEFAULT_FOLDER) return keep;
	return [folder, ...keep];
}

interface FolderEntry {
	readonly name: string;
	readonly notes: readonly NoteRecord[];
}

// ─────────────────────────────────────────────────────────────────────
// Nav state machine

type View_ =
	| { readonly kind: "root" }
	| { readonly kind: "folder"; readonly folder: string }
	| {
			readonly kind: "note";
			readonly folder: string;
			readonly noteId: string;
	  };

export interface NotesPanelProps {
	/** Live, most-recently-edited-first list from `useNotes(persister)`. */
	readonly notes: readonly NoteRecord[];
	/** LOCAL-FIRST upsert; host persists + mirrors to the DeviceGroup. */
	readonly onUpsert: (note: NoteRecord) => void;
	/** Delete by id; host removes locally + sends `com.ping.note_delete`. */
	readonly onDelete: (id: string) => void;
	/** Mints a fresh stable hex `note_id`. */
	readonly makeNoteId: () => string;
	/** Per-note sync state (reserved for status chrome). */
	readonly syncState?: NotesSyncState | undefined;
}

export const NotesPanel: React.FC<NotesPanelProps> = ({
	notes,
	onUpsert,
	onDelete,
	makeNoteId,
}) => {
	const [view, setView] = useState<View_>({ kind: "root" });
	// Folders that exist but hold no notes yet (created via "New Folder").
	// TODO(notes): these do NOT persist and vanish on reload. Folders are
	// derived purely from note tags (`folderOf`), and the only persistence
	// surface exposed to this component is `onUpsert(NoteRecord)` — an empty
	// folder has no note to carry a tag, and `NoteRecord` / the §2.10 wire
	// (NOTE_UPSERT/NOTE_DELETE) have no folder concept. Durable empty folders
	// need a new host-owned persistence channel (e.g. a folder envelope kind
	// or a dedicated persister prop); it can't be done from the current prop
	// surface without minting a phantom placeholder note. Kept as in-session
	// state so the create-folder UX still works until that channel exists.
	const [emptyFolders, setEmptyFolders] = useState<readonly string[]>([]);

	// Group notes by their derived folder, then fold in remembered-empty
	// folders. The default folder is always present.
	const folders = useMemo<readonly FolderEntry[]>(() => {
		const byName = new Map<string, NoteRecord[]>();
		byName.set(DEFAULT_FOLDER, []);
		for (const n of notes) {
			const f = folderOf(n);
			const list = byName.get(f);
			if (list === undefined) byName.set(f, [n]);
			else list.push(n);
		}
		for (const f of emptyFolders) {
			if (!byName.has(f)) byName.set(f, []);
		}
		return [...byName.entries()].map(([name, list]) => ({
			name,
			// Most-recent first (the live list is already sorted, but a
			// folder slice should keep that order independent of grouping).
			notes: list.slice().sort((a, b) => b.updatedAt - a.updatedAt),
		}));
	}, [notes, emptyFolders]);

	const findFolder = useCallback(
		(name: string): FolderEntry =>
			folders.find((f) => f.name === name) ?? { name, notes: [] },
		[folders],
	);

	const handleCreateFolder = useCallback((name: string) => {
		const trimmed = name.trim();
		if (trimmed.length === 0) return;
		setEmptyFolders((prev) =>
			prev.includes(trimmed) ? prev : [...prev, trimmed],
		);
	}, []);

	const handleDeleteFolder = useCallback(
		(name: string) => {
			if (name === DEFAULT_FOLDER) return;
			setEmptyFolders((prev) => prev.filter((f) => f !== name));
			// Member notes move back to "My notes" (strip the folder tag,
			// preserving `favorite`).
			for (const n of findFolder(name).notes) {
				const tags = tagsForFolder(n, DEFAULT_FOLDER);
				onUpsert(
					tags.length > 0
						? { ...n, tags, updatedAt: Date.now() }
						: {
								id: n.id,
								title: n.title,
								body: n.body,
								updatedAt: Date.now(),
							},
				);
			}
		},
		[findFolder, onUpsert],
	);

	const handleRenameFolder = useCallback(
		(from: string, to: string) => {
			const next = to.trim();
			if (from === DEFAULT_FOLDER || next.length === 0 || next === from) {
				return;
			}
			// Rewrite the folder tag on every member note, preserving favorite.
			// A pure rename only re-tags — it must NOT bump `updatedAt`, which
			// would falsely mark every note "modified" now and reorder the
			// list. Spread `...n` so each note keeps its existing `updatedAt`.
			for (const n of findFolder(from).notes) {
				const tags = tagsForFolder(n, next);
				onUpsert({ ...n, tags });
			}
			// Carry over a remembered-empty folder under its new name.
			setEmptyFolders((prev) => {
				if (!prev.includes(from)) return prev;
				const without = prev.filter((f) => f !== from);
				return without.includes(next) ? without : [...without, next];
			});
		},
		[findFolder, onUpsert],
	);

	const handleNewNote = useCallback(
		(folder: string) => {
			const id = makeNoteId();
			const base: NoteRecord = {
				id,
				title: "",
				body: "",
				updatedAt: Date.now(),
			};
			const tags = tagsForFolder(base, folder);
			onUpsert(tags.length > 0 ? { ...base, tags } : base);
			setView({ kind: "note", folder, noteId: id });
		},
		[makeNoteId, onUpsert],
	);

	// ── ROOT — folder tree ─────────────────────────────────────────────
	if (view.kind === "root") {
		const hasContent = notes.length > 0 || emptyFolders.length > 0;
		return (
			<View
				className="flex-1 h-full min-h-0 self-stretch bg-light-surface-primary dark:bg-dark-surface-app"
			>
				{hasContent ? (
					<RootTree
						folders={folders}
						onOpenFolder={(folder) => setView({ kind: "folder", folder })}
						onCreateFolder={handleCreateFolder}
						onDeleteFolder={handleDeleteFolder}
						onRenameFolder={handleRenameFolder}
					/>
				) : (
					<EmptyState onNewNote={() => handleNewNote(DEFAULT_FOLDER)} />
				)}
			</View>
		);
	}

	// ── FOLDER — note list ─────────────────────────────────────────────
	if (view.kind === "folder") {
		const entry = findFolder(view.folder);
		return (
			<View
				className="flex-1 h-full min-h-0 self-stretch bg-light-surface-primary dark:bg-dark-surface-app"
			>
				<FolderView
					folder={entry}
					onBack={() => setView({ kind: "root" })}
					onOpenNote={(noteId) =>
						setView({ kind: "note", folder: view.folder, noteId })
					}
					onNewNote={() => handleNewNote(view.folder)}
				/>
			</View>
		);
	}

	// ── NOTE — editor ──────────────────────────────────────────────────
	const note = notes.find((n) => n.id === view.noteId) ?? null;
	return (
		<View
			className="flex-1 h-full min-h-0 self-stretch bg-light-surface-primary dark:bg-dark-surface-app"
		>
			{note !== null ? (
				<NoteEditor
					key={note.id}
					note={note}
					onBack={() => setView({ kind: "folder", folder: view.folder })}
					onSave={onUpsert}
					onDelete={() => {
						onDelete(note.id);
						setView({ kind: "folder", folder: view.folder });
					}}
				/>
			) : (
				// Note was deleted out from under us — fall back to the folder.
				<FolderView
					folder={findFolder(view.folder)}
					onBack={() => setView({ kind: "root" })}
					onOpenNote={(noteId) =>
						setView({ kind: "note", folder: view.folder, noteId })
					}
					onNewNote={() => handleNewNote(view.folder)}
				/>
			)}
		</View>
	);
};

// ─────────────────────────────────────────────────────────────────────
// Header — shared chrome (title / breadcrumb + right-side actions)

interface PanelHeaderProps {
	readonly children: React.ReactNode;
}

function PanelHeader({ children }: PanelHeaderProps): JSX.Element {
	return (
		<View className="flex flex-row items-center justify-between h-14 px-lg border-b border-black/[0.06] dark:border-white/[0.06]">
			{children}
		</View>
	);
}

function useIconColor(): { primary: string; muted: string } {
	const scheme = useColorScheme();
	return scheme === "dark"
		? { primary: "#FFFFFF", muted: "#8A8F98" }
		: { primary: "#1B1D22", muted: "#6B7280" };
}

// ─────────────────────────────────────────────────────────────────────
// ROOT — folder tree

interface RootTreeProps {
	readonly folders: readonly FolderEntry[];
	readonly onOpenFolder: (folder: string) => void;
	readonly onCreateFolder: (name: string) => void;
	readonly onDeleteFolder: (name: string) => void;
	readonly onRenameFolder: (from: string, to: string) => void;
}

function RootTree({
	folders,
	onOpenFolder,
	onCreateFolder,
	onDeleteFolder,
	onRenameFolder,
}: RootTreeProps): JSX.Element {
	const { primary, muted } = useIconColor();
	const [adding, setAdding] = useState(false);
	const [draft, setDraft] = useState("");

	const commit = useCallback(() => {
		onCreateFolder(draft);
		setDraft("");
		setAdding(false);
	}, [draft, onCreateFolder]);

	return (
		<View className="flex-1">
			<PanelHeader>
				<Text className="font-sans text-[16px] leading-[22px] font-bold text-light-text-primary dark:text-[#F5F6F8]">
					Notes
				</Text>
				<View className="flex flex-row items-center gap-md">
					<Pressable
						accessibilityRole="button"
						accessibilityLabel="Search notes"
						hitSlop={8}
						className="active:opacity-60"
					>
						<Search size={20} color={muted} strokeWidth={1.75} />
					</Pressable>
					<Pressable
						accessibilityRole="button"
						accessibilityLabel="New Folder"
						onPress={() => setAdding(true)}
						className="flex flex-row items-center gap-xs px-3 h-8 rounded-[8px] bg-light-surface-elevated dark:bg-dark-surface-elevated active:opacity-80"
					>
						<Text className="font-sans text-[12px] leading-[17px] font-bold text-light-text-primary dark:text-white">
							New Folder
						</Text>
						<Plus size={18} color={primary} strokeWidth={2} />
					</Pressable>
				</View>
			</PanelHeader>

			<View className="px-sm pt-sm">
				{adding ? (
					<View className="h-12 px-md mb-1 flex flex-row items-center rounded-[12px] bg-black/[0.04] dark:bg-white/[0.05]">
						<TextInput
							autoFocus
							value={draft}
							onChangeText={setDraft}
							onSubmitEditing={commit}
							onBlur={commit}
							placeholder="Folder name"
							placeholderTextColor={muted}
							accessibilityLabel="Folder name"
							className="flex-1 p-0 font-sans text-[16px] leading-[22px] text-light-text-primary dark:text-[#F5F6F8]"
						/>
					</View>
				) : null}

				{folders.map((folder) => (
					<FolderRow
						key={folder.name}
						folder={folder}
						deletable={folder.name !== DEFAULT_FOLDER}
						onPress={() => onOpenFolder(folder.name)}
						onDelete={() => onDeleteFolder(folder.name)}
						onRename={(to) => onRenameFolder(folder.name, to)}
					/>
				))}
			</View>
		</View>
	);
}

interface FolderRowProps {
	readonly folder: FolderEntry;
	readonly deletable: boolean;
	readonly onPress: () => void;
	readonly onDelete: () => void;
	readonly onRename: (to: string) => void;
}

function FolderRow({
	folder,
	deletable,
	onPress,
	onDelete,
	onRename,
}: FolderRowProps): JSX.Element {
	const { muted } = useIconColor();
	const [menuOpen, setMenuOpen] = useState(false);
	const [renaming, setRenaming] = useState(false);
	const [draft, setDraft] = useState(folder.name);

	const commitRename = useCallback(() => {
		onRename(draft);
		setRenaming(false);
	}, [draft, onRename]);

	// Inline rename row: replaces the label with an editable field.
	if (renaming) {
		return (
			<View className="h-12 px-md mb-1 flex flex-row items-center rounded-[12px] bg-black/[0.04] dark:bg-white/[0.05]">
				<ChevronDown size={20} color={muted} strokeWidth={2} />
				<TextInput
					autoFocus
					value={draft}
					onChangeText={setDraft}
					onSubmitEditing={commitRename}
					onBlur={commitRename}
					placeholder="Folder name"
					placeholderTextColor={muted}
					accessibilityLabel="Rename folder"
					className="ml-sm flex-1 p-0 font-sans text-[16px] leading-[22px] text-light-text-primary dark:text-[#F5F6F8]"
				/>
			</View>
		);
	}

	return (
		<Pressable
			accessibilityRole="button"
			accessibilityLabel={folder.name}
			onPress={onPress}
			className="h-12 px-md mb-1 flex flex-row items-center rounded-[12px] active:bg-black/[0.04] dark:active:bg-white/[0.05]"
		>
			<ChevronDown size={20} color={muted} strokeWidth={2} />
			<Text className="ml-sm flex-1 font-sans text-[16px] leading-[22px] text-light-text-primary dark:text-[#F5F6F8]">
				{folder.name}
			</Text>
			{deletable ? (
				<View>
					<Pressable
						accessibilityRole="button"
						accessibilityLabel="Folder options"
						accessibilityState={{ expanded: menuOpen }}
						hitSlop={8}
						onPress={() => setMenuOpen((v) => !v)}
						className="px-xs active:opacity-60"
					>
						<MoreVertical size={20} color={muted} strokeWidth={2} />
					</Pressable>
					{menuOpen ? (
						<View className="absolute right-0 top-[28px] z-10 min-w-[140px] rounded-[10px] border border-black/[0.08] bg-white dark:border-white/[0.08] dark:bg-dark-surface-elevated py-xs">
							<Pressable
								accessibilityRole="button"
								accessibilityLabel="Rename folder"
								onPress={() => {
									setMenuOpen(false);
									setDraft(folder.name);
									setRenaming(true);
								}}
								className="px-md py-sm active:bg-black/[0.04] dark:active:bg-white/[0.06]"
							>
								<Text className="font-sans text-footnote font-semibold text-light-text-primary dark:text-[#F5F6F8]">
									Rename folder
								</Text>
							</Pressable>
							<Pressable
								accessibilityRole="button"
								accessibilityLabel="Delete folder"
								onPress={() => {
									setMenuOpen(false);
									onDelete();
								}}
								className="px-md py-sm active:bg-black/[0.04] dark:active:bg-white/[0.06]"
							>
								<Text className="font-sans text-footnote font-semibold text-light-accent-rose dark:text-dark-accent-rose">
									Delete folder
								</Text>
							</Pressable>
						</View>
					) : null}
				</View>
			) : (
				<MoreVertical size={20} color={muted} strokeWidth={2} />
			)}
		</Pressable>
	);
}

// ─────────────────────────────────────────────────────────────────────
// FOLDER — note list

interface FolderViewProps {
	readonly folder: FolderEntry;
	readonly onBack: () => void;
	readonly onOpenNote: (noteId: string) => void;
	readonly onNewNote: () => void;
}

function FolderView({
	folder,
	onBack,
	onOpenNote,
	onNewNote,
}: FolderViewProps): JSX.Element {
	const { primary, muted } = useIconColor();

	const renderItem: ListRenderItem<NoteRecord> = useCallback(
		({ item }) => (
			<NoteListRow note={item} onPress={() => onOpenNote(item.id)} />
		),
		[onOpenNote],
	);
	const keyExtractor = useCallback((n: NoteRecord) => n.id, []);

	return (
		<View className="flex-1">
			<PanelHeader>
				<Breadcrumb segments={["Notes", folder.name]} onFirst={onBack} />
				<View className="flex flex-row items-center gap-md">
					<Pressable
						accessibilityRole="button"
						accessibilityLabel="Search notes"
						hitSlop={8}
						className="active:opacity-60"
					>
						<Search size={20} color={muted} strokeWidth={1.75} />
					</Pressable>
					<Pressable
						accessibilityRole="button"
						accessibilityLabel="New note"
						onPress={onNewNote}
						className="flex flex-row items-center gap-xs px-3 h-8 rounded-[8px] bg-light-surface-elevated dark:bg-dark-surface-elevated active:opacity-80"
					>
						<Text className="font-sans text-[12px] leading-[17px] font-bold text-light-text-primary dark:text-white">
							New note
						</Text>
						<Plus size={18} color={primary} strokeWidth={2} />
					</Pressable>
				</View>
			</PanelHeader>

			{folder.notes.length === 0 ? (
				<View className="flex-1 items-center justify-center px-lg">
					<Text className="font-sans text-footnote text-[#6B7280] dark:text-white/45 text-center">
						No notes in this folder yet.
					</Text>
				</View>
			) : (
				<FlatList<NoteRecord>
					data={folder.notes}
					renderItem={renderItem}
					keyExtractor={keyExtractor}
				/>
			)}
		</View>
	);
}

interface NoteListRowProps {
	readonly note: NoteRecord;
	readonly onPress: () => void;
}

function NoteListRow({ note, onPress }: NoteListRowProps): JSX.Element {
	const preview = buildPreview(note.body);
	return (
		<Pressable
			accessibilityRole="button"
			accessibilityLabel={note.title || "Untitled note"}
			onPress={onPress}
			className="flex flex-row items-start px-lg py-md active:bg-black/[0.03] dark:active:bg-white/[0.04]"
		>
			<View className="flex-1 pr-md">
				<Text
					numberOfLines={1}
					className="font-sans text-[16px] leading-[22px] font-bold text-light-text-primary dark:text-[#F5F6F8]"
				>
					{note.title || "Untitled note"}
				</Text>
				<Text
					numberOfLines={1}
					className="mt-[2px] font-sans text-[12px] leading-[17px] text-[#B5BAC3]"
				>
					{preview}
				</Text>
			</View>
			<Text className="mt-[2px] font-sans text-[12px] leading-[17px] text-light-text-muted dark:text-dark-text-muted">
				{formatShortDate(note.updatedAt)}
			</Text>
		</Pressable>
	);
}

// ─────────────────────────────────────────────────────────────────────
// NOTE — editor

interface NoteEditorProps {
	readonly note: NoteRecord;
	readonly onBack: () => void;
	readonly onSave: (note: NoteRecord) => void;
	readonly onDelete: () => void;
}

function NoteEditor({
	note,
	onBack,
	onSave,
	onDelete,
}: NoteEditorProps): JSX.Element {
	const { muted } = useIconColor();
	const [title, setTitle] = useState(note.title);
	const [body, setBody] = useState(note.body);
	const [menuOpen, setMenuOpen] = useState(false);
	const latest = useRef({ title, body });
	latest.current = { title, body };

	// Track the body selection so toolbar wraps apply to the active range.
	// `controlledSel` is set non-null only right after a toolbar edit so we
	// can imperatively re-place the caret; on the next selection change we
	// release control back to the platform.
	const selRef = useRef<MdSelection>({ start: 0, end: 0 });
	const [controlledSel, setControlledSel] = useState<MdSelection | null>(null);

	const applyEdit = useCallback(
		(next: { text: string; selection: MdSelection }) => {
			setBody(next.text);
			latest.current = { ...latest.current, body: next.text };
			selRef.current = next.selection;
			setControlledSel(next.selection);
		},
		[],
	);

	const onWrap = useCallback(
		(kind: WrapKind) => applyEdit(toggleWrap(latest.current.body, selRef.current, kind)),
		[applyEdit],
	);
	const onPrefix = useCallback(
		(kind: "bullet" | "ordered" | "indent") =>
			applyEdit(toggleLinePrefix(latest.current.body, selRef.current, kind)),
		[applyEdit],
	);
	const onCodeBlock = useCallback(
		() => applyEdit(insertCodeBlock(latest.current.body, selRef.current)),
		[applyEdit],
	);
	const onLink = useCallback(
		(url: string) =>
			applyEdit(insertLink(latest.current.body, selRef.current, url)),
		[applyEdit],
	);

	const handleSave = useCallback(() => {
		onSave({
			id: note.id,
			title: latest.current.title,
			body: latest.current.body,
			updatedAt: Date.now(),
			...(note.tags !== undefined ? { tags: note.tags } : {}),
		});
	}, [note.id, note.tags, onSave]);

	// Persist edits on back-navigation AND on unmount (view switch / panel
	// close) so typing into a note and leaving without pressing Save no longer
	// silently discards the draft. Guarded by a dirty check against the note as
	// last persisted, so merely opening + closing a note doesn't bump its
	// `updatedAt` (which would reorder the list on a pure view).
	const savedRef = useRef({ title: note.title, body: note.body });
	const saveRef = useRef(handleSave);
	saveRef.current = handleSave;
	const flushIfDirty = useCallback(() => {
		const cur = latest.current;
		if (
			cur.title !== savedRef.current.title ||
			cur.body !== savedRef.current.body
		) {
			savedRef.current = { title: cur.title, body: cur.body };
			saveRef.current();
		}
	}, []);
	useEffect(() => flushIfDirty, [flushIfDirty]);
	const handleBack = useCallback(() => {
		flushIfDirty();
		onBack();
	}, [flushIfDirty, onBack]);

	const crumbTitle = title.trim().length > 0 ? title.trim() : "New note";

	return (
		<View className="flex-1">
			<PanelHeader>
				<Breadcrumb segments={["Notes", crumbTitle]} onFirst={handleBack} />
				<View className="flex flex-row items-center gap-sm">
					<View>
						<Pressable
							accessibilityRole="button"
							accessibilityLabel="Note options"
							accessibilityState={{ expanded: menuOpen }}
							hitSlop={8}
							onPress={() => setMenuOpen((v) => !v)}
							className="px-xs active:opacity-60"
						>
							<MoreVertical size={20} color={muted} strokeWidth={2} />
						</Pressable>
						{menuOpen ? (
							<View className="absolute right-0 top-[28px] z-10 min-w-[140px] rounded-[10px] border border-black/[0.08] bg-white dark:border-white/[0.08] dark:bg-dark-surface-elevated py-xs">
								<Pressable
									accessibilityRole="button"
									accessibilityLabel="Delete note"
									onPress={() => {
										setMenuOpen(false);
										onDelete();
									}}
									className="px-md py-sm active:bg-black/[0.04] dark:active:bg-white/[0.06]"
								>
									<Text className="font-sans text-footnote font-semibold text-light-accent-rose">
										Delete note
									</Text>
								</Pressable>
							</View>
						) : null}
					</View>
					<Pressable
						accessibilityRole="button"
						accessibilityLabel="Save note"
						onPress={handleSave}
						className="px-md h-9 items-center justify-center rounded-full bg-light-accent-brand dark:bg-dark-accent-brand active:opacity-80"
					>
						<Text className="font-sans text-[14px] leading-[20px] font-bold text-white">
							Save
						</Text>
					</Pressable>
				</View>
			</PanelHeader>

			<TextInput
				value={title}
				onChangeText={setTitle}
				placeholder="Title"
				placeholderTextColor={muted}
				accessibilityLabel="Note title"
				className="px-lg pt-lg pb-xs font-sans text-[16px] leading-[22px] font-bold text-light-text-primary dark:text-[#F5F6F8]"
			/>
			<Text className="px-lg pb-sm font-sans text-[12px] leading-[17px] text-light-text-muted dark:text-dark-text-muted">
				{formatLongDate(note.updatedAt)}
			</Text>

			<TextInput
				value={body}
				onChangeText={(t) => {
					setBody(t);
					latest.current = { ...latest.current, body: t };
				}}
				selection={controlledSel ?? undefined}
				onSelectionChange={(e) => {
					selRef.current = e.nativeEvent.selection;
					// Release imperative control once the platform reports the
					// caret we asked for, so manual cursor moves work again.
					if (controlledSel !== null) setControlledSel(null);
				}}
				placeholder="Just write something down…"
				placeholderTextColor={muted}
				accessibilityLabel="Note body"
				multiline
				textAlignVertical="top"
				className="flex-1 px-lg py-sm font-sans text-[16px] leading-[22px] text-light-text-primary dark:text-[#F5F6F8]"
			/>

			<FormattingToolbar
				onWrap={onWrap}
				onPrefix={onPrefix}
				onCodeBlock={onCodeBlock}
				onLink={onLink}
			/>
		</View>
	);
}

// Functional formatting toolbar matching the Figma editor chrome
// (B / I / U / S / bulleted / ordered / link / code / code-block /
// indent). Each button rewrites the markdown `body` via the pure
// transforms in `markdownEdit.ts`. The link button reveals an inline
// URL input (RN has no `window.prompt`).

interface FormattingToolbarProps {
	readonly onWrap: (kind: WrapKind) => void;
	readonly onPrefix: (kind: "bullet" | "ordered" | "indent") => void;
	readonly onCodeBlock: () => void;
	readonly onLink: (url: string) => void;
}

function FormattingToolbar({
	onWrap,
	onPrefix,
	onCodeBlock,
	onLink,
}: FormattingToolbarProps): JSX.Element {
	const { primary, muted } = useIconColor();
	const [linkOpen, setLinkOpen] = useState(false);
	const [linkUrl, setLinkUrl] = useState("");

	const commitLink = useCallback(() => {
		const url = linkUrl.trim();
		if (url.length > 0) onLink(url);
		setLinkUrl("");
		setLinkOpen(false);
	}, [linkUrl, onLink]);

	const buttons: ReadonlyArray<{
		Icon: typeof Bold;
		label: string;
		onPress: () => void;
	}> = [
		{ Icon: Bold, label: "Bold", onPress: () => onWrap("bold") },
		{ Icon: Italic, label: "Italic", onPress: () => onWrap("italic") },
		{ Icon: Underline, label: "Underline", onPress: () => onWrap("underline") },
		{
			Icon: Strikethrough,
			label: "Strikethrough",
			onPress: () => onWrap("strike"),
		},
		{ Icon: List, label: "Bulleted list", onPress: () => onPrefix("bullet") },
		{
			Icon: ListOrdered,
			label: "Numbered list",
			onPress: () => onPrefix("ordered"),
		},
		{ Icon: LinkIcon, label: "Link", onPress: () => setLinkOpen((v) => !v) },
		{ Icon: Code, label: "Inline code", onPress: () => onWrap("code") },
		{ Icon: SquareCode, label: "Code block", onPress: onCodeBlock },
		{
			Icon: IndentIncrease,
			label: "Indent",
			onPress: () => onPrefix("indent"),
		},
	];

	return (
		<View className="mx-lg mb-lg">
			{linkOpen ? (
				<View className="flex flex-row items-center gap-sm mb-xs px-md h-11 rounded-[12px] bg-black/[0.04] dark:bg-white/[0.06]">
					<TextInput
						autoFocus
						value={linkUrl}
						onChangeText={setLinkUrl}
						onSubmitEditing={commitLink}
						placeholder="https://…"
						placeholderTextColor={muted}
						accessibilityLabel="Link URL"
						autoCapitalize="none"
						autoCorrect={false}
						keyboardType="url"
						className="flex-1 p-0 font-sans text-[14px] leading-[20px] text-light-text-primary dark:text-[#F5F6F8]"
					/>
					<Pressable
						accessibilityRole="button"
						accessibilityLabel="Insert link"
						onPress={commitLink}
						className="px-md h-8 items-center justify-center rounded-full bg-light-accent-brand dark:bg-dark-accent-brand active:opacity-80"
					>
						<Text className="font-sans text-[13px] font-semibold text-white">
							Add
						</Text>
					</Pressable>
				</View>
			) : null}
			<View className="flex flex-row items-center gap-xs px-sm h-11 rounded-[12px] bg-black/[0.04] dark:bg-white/[0.06]">
				{buttons.map(({ Icon, label, onPress }) => (
					<Pressable
						key={label}
						accessibilityRole="button"
						accessibilityLabel={label}
						onPress={onPress}
						className="w-8 h-8 items-center justify-center rounded-md active:bg-black/[0.06] dark:active:bg-white/[0.08]"
					>
						<Icon size={18} color={primary} strokeWidth={2} />
					</Pressable>
				))}
			</View>
		</View>
	);
}

// ─────────────────────────────────────────────────────────────────────
// EMPTY state (no notes + no folders)

interface EmptyStateProps {
	readonly onNewNote: () => void;
}

function EmptyState({ onNewNote }: EmptyStateProps): JSX.Element {
	return (
		<View className="flex-1">
			<PanelHeader>
				<Text className="font-sans text-[16px] leading-[22px] font-bold text-light-text-primary dark:text-[#F5F6F8]">
					Notes
				</Text>
				<View className="flex flex-row items-center gap-md">
					<Search size={20} color="#6B7280" strokeWidth={1.75} />
					<Pressable
						accessibilityRole="button"
						accessibilityLabel="New Note"
						onPress={onNewNote}
						className="flex flex-row items-center gap-xs px-3 h-8 rounded-[8px] bg-light-surface-elevated dark:bg-dark-surface-elevated active:opacity-80"
					>
						<Text className="font-sans text-[12px] leading-[17px] font-bold text-light-text-primary dark:text-white">
							New Note
						</Text>
						<Plus size={18} color="#FFFFFF" strokeWidth={2} />
					</Pressable>
				</View>
			</PanelHeader>

			<View className="flex-1 items-center justify-center px-xl">
				<PingLogomark size={120} accessibilityLabel="Ping notes" />
				<Text className="mt-lg max-w-[280px] text-center font-sans text-[16px] leading-[22px] text-light-text-muted dark:text-dark-text-muted">
					Just write something down — Arthur will see it the moment you do.
				</Text>
				<Pressable
					accessibilityRole="button"
					accessibilityLabel="New note"
					onPress={onNewNote}
					className="mt-lg flex flex-row items-center gap-xs px-lg h-11 rounded-full bg-light-accent-brand dark:bg-dark-accent-brand active:opacity-80"
				>
					<Text className="font-sans text-[16px] font-semibold text-white">
						New note
					</Text>
					<Plus size={18} color="#FFFFFF" strokeWidth={2.25} />
				</Pressable>
			</View>
		</View>
	);
}

// ─────────────────────────────────────────────────────────────────────
// Breadcrumb

interface BreadcrumbProps {
	readonly segments: readonly string[];
	readonly onFirst: () => void;
}

function Breadcrumb({ segments, onFirst }: BreadcrumbProps): JSX.Element {
	const { muted } = useIconColor();
	const last = segments.length - 1;
	return (
		<View className="flex flex-row items-center gap-xs flex-1 mr-md">
			<Pressable
				accessibilityRole="button"
				accessibilityLabel="Back"
				hitSlop={8}
				onPress={onFirst}
				className="active:opacity-60"
			>
				<ChevronLeft size={20} color={muted} strokeWidth={2} />
			</Pressable>
			{segments.map((seg, i) => (
				<View key={`${seg}-${i}`} className="flex flex-row items-center">
					{i === 0 ? (
						<Pressable
							accessibilityRole="button"
							accessibilityLabel={seg}
							onPress={onFirst}
							className="active:opacity-60"
						>
							<Text className="font-sans text-[14px] leading-[20px] text-[#B5BAC3]">
								{seg}
							</Text>
						</Pressable>
					) : (
						<>
							<Text className="mx-xs font-sans text-[14px] leading-[20px] text-[#6B7280]">
								›
							</Text>
							<Text
								numberOfLines={1}
								className={`font-sans text-[14px] leading-[20px] ${
									i === last
										? "font-bold text-light-text-primary dark:text-[#F5F6F8]"
										: "text-[#B5BAC3]"
								}`}
							>
								{seg}
							</Text>
						</>
					)}
				</View>
			))}
		</View>
	);
}

// ─────────────────────────────────────────────────────────────────────
// Date helpers (DD/MM short form per Figma; long form for the editor).

function formatShortDate(ms: number): string {
	try {
		const d = new Date(ms);
		const dd = String(d.getDate()).padStart(2, "0");
		const mm = String(d.getMonth() + 1).padStart(2, "0");
		return `${dd}/${mm}`;
	} catch {
		return "";
	}
}

function formatLongDate(ms: number): string {
	try {
		return new Date(ms).toLocaleDateString(undefined, {
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	} catch {
		return "";
	}
}
